import { useBusinessDocumentsContext } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/context/BusinessDocumentsContext';
import {
  getShownMismatchCaptionForDefaultField,
  type ReportInferencesMismatchViewData,
} from '@/hooks/report_inferences/useReportInferencesMismatchCaptions';
import {
  ListViewBody,
  ListViewLabel,
  Single,
} from '@moneyforward/ap-frontend-components';
import {
  ApprovingInvoiceReportFormInputResponse,
  ApprovingInvoiceReportPaymentInfoResponse,
  reportFormInputType,
} from 'ap-openapi';
import classNames from 'classnames/bind';
import { FC, memo } from 'react';
import styles from '.././styles.module.scss';
import {
  BusinessDocumentsFieldView,
  CombineLabelFieldView,
  CombineLabelFieldViewWithLink,
  DefaultFieldView,
  FileFieldView,
  InvoiceFileFieldView,
  InvoiceRegistrationNumberFieldView,
  TextAreaFieldView,
} from './index';

const cx = classNames.bind(styles);

// Main switch function
const switchViewType = (
  type: string,
  item: ApprovingInvoiceReportFormInputResponse,
  viewData: ReportInferencesMismatchViewData | undefined,
  reportFormInputs:
    | ApprovingInvoiceReportFormInputResponse[]
    | null
    | undefined,
  itemIndex: number
) => {
  switch (type) {
    case reportFormInputType.ApReportFormInputFileField:
      return <FileFieldView item={item} />;
    case reportFormInputType.ApReportFormInputBusinessDocumentsField:
      return <BusinessDocumentsFieldView />;
    case reportFormInputType.ApReportFormInputInvoiceFileField:
      return <InvoiceFileFieldView item={item} />;
    case reportFormInputType.ApReportFormInputCheckBox:
      return <CombineLabelFieldView item={item} />;
    case reportFormInputType.ApReportFormInputReportNumberForExpenseField:
    case reportFormInputType.ApReportFormInputContractNumberField:
      return <CombineLabelFieldViewWithLink item={item} />;
    case reportFormInputType.ApReportFormInputInvoiceRegistrationNumberField: {
      const mismatchCaption = viewData?.fieldCaptionsByInputId[item?.id ?? ''];
      return (
        <InvoiceRegistrationNumberFieldView
          item={item}
          mismatchCaption={mismatchCaption}
        />
      );
    }
    case reportFormInputType.ApReportFormInputTextArea:
      return <TextAreaFieldView item={item} />;
    default: {
      const mismatchCaption = viewData?.fieldCaptionsByInputId[item?.id ?? ''];
      const shownMismatchCaption = getShownMismatchCaptionForDefaultField(
        item,
        mismatchCaption,
        reportFormInputs,
        itemIndex
      );
      return (
        <DefaultFieldView item={item} mismatchCaption={shownMismatchCaption} />
      );
    }
  }
};

type SmartListViewDetailsProps = {
  data: ApprovingInvoiceReportPaymentInfoResponse;
  mismatchViewData: ReportInferencesMismatchViewData | undefined;
};

export const SmartListViewDetails: FC<SmartListViewDetailsProps> = memo(
  ({ data, mismatchViewData }) => {
    const { isVisible } = useBusinessDocumentsContext();

    return (
      <ListViewBody className={cx('applicationContentContainer')}>
        {data?.report_form_inputs?.map((item, index) => {
          if (!isVisible(item)) {
            return null;
          }

          return (
            <div
              key={item?.label ?? `empty-${index}`}
              className={cx('singleContainer')}
            >
              <Single>
                <ListViewLabel key='left'>{item?.label ?? ''}</ListViewLabel>
                <ListViewBody
                  className={cx('listViewBodyContainer')}
                  key='right'
                >
                  {switchViewType(
                    item?.type ?? '',
                    item,
                    mismatchViewData,
                    data?.report_form_inputs,
                    index
                  )}
                </ListViewBody>
              </Single>
            </div>
          );
        })}
      </ListViewBody>
    );
  }
);
SmartListViewDetails.displayName = 'SmartListViewDetails';
